import { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { CircularProgress } from "@mui/material";
import { FaInstagram, FaLock } from "react-icons/fa";
import { FaMeta } from "react-icons/fa6";
import { toast } from "react-toastify";
import { useGetOnboarding } from "hooks/useGetOnboarding";
import ChannelConnectionsAdminService from "services/admin/channelConnections";
import { buildInstagramAuthURL } from "pages/Admin/AIAgents/components/InstagramConnect";
import {
  INSTAGRAM_GRADIENT,
  InstagramConnectLayout,
} from "./components/InstagramConnectLayout";

/**
 * «Подключите Instagram» — полноэкранная страница без меню и шапки, перед входом через Instagram.
 * Из «Быстрого старта» открывается с ?returnTo=onboarding: после входа сразу создаём чат-бота на кодовое слово.
 * Канал берём тот, что ждёт входа, или создаём новый — только по нажатию кнопки.
 */
const InstagramConnectPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const orgID = localStorage.getItem("orgID");
  const returnTo =
    searchParams.get("returnTo") === "onboarding" ? "onboarding" : undefined;
  const { data: onboarding } = useGetOnboarding(orgID);
  const [isRedirecting, setIsRedirecting] = useState(false);

  const goBack = () => {
    // Открыли по прямой ссылке — назад на дашборд
    if (window.history.state?.idx > 0) navigate(-1);
    else navigate("/admin/stats");
  };

  const login = async () => {
    if (!orgID) return;
    try {
      setIsRedirecting(true);
      let channelConnectionID =
        onboarding?.instagram && !onboarding.instagram.connected
          ? onboarding.instagram.channelConnectionID
          : null;
      if (!channelConnectionID) {
        const { data: created } = await ChannelConnectionsAdminService.post({
          source: "INSTAGRAM",
        });
        channelConnectionID = created.id;
      }
      window.location.href = buildInstagramAuthURL(
        { id: channelConnectionID!, orgID, agentID: null },
        returnTo,
      );
    } catch (error: any) {
      setIsRedirecting(false);
      toast.error(
        error?.response?.data?.error?.message ||
          "Не удалось начать подключение Instagram",
      );
    }
  };

  const connectedUsername = onboarding?.instagram?.connected
    ? onboarding.instagram.username
    : null;

  return (
    <InstagramConnectLayout
      title="Войдите в Instagram"
      subtitle="И выдайте разрешения для Ubee AI"
      onBack={goBack}
    >
      <h2 className="text-2xl sm:text-[28px] font-bold leading-snug mb-3">
        Войдите в Instagram и выдайте разрешения для Ubee AI
      </h2>
      <p className="text-gray-500 mb-6">
        Это позволит Ubee AI отвечать клиентам в Директ и на комментарии:
        чат-бот по кодовым словам и ИИ-агент. Вы всегда контролируете свои
        данные и можете отключить доступ в любой момент.
      </p>

      <div className="inline-flex items-center gap-2 rounded-xl border px-4 py-2.5 mb-4">
        <FaMeta className="text-[#0866ff]" size={26} />
        <span className="text-xl font-semibold text-[#1c2b33]">Meta</span>
        <span className="text-gray-500 text-sm">
          Официальный технологический провайдер
        </span>
      </div>
      <p className="text-gray-500 mb-6">
        Подключение проходит через официальный API Meta — вы не передаёте логин
        и пароль в Ubee AI. Это безопасно для вашего аккаунта.
      </p>

      <div className="rounded-xl bg-amber-50 border border-amber-100 px-4 py-3 mb-8 text-sm text-gray-700">
        <b>Нужен профессиональный аккаунт</b> (Бизнес или Автор). Переключить
        можно в Instagram: Настройки → Тип аккаунта и инструменты →
        Переключиться на профессиональный аккаунт.
      </div>

      {connectedUsername && (
        <div className="text-sm text-green-700 mb-3">
          Уже подключён @{connectedUsername}. Можно войти в другой аккаунт.
        </div>
      )}

      <button
        type="button"
        onClick={login}
        disabled={isRedirecting || !orgID}
        className="w-full flex items-center justify-center gap-3 rounded-xl border border-gray-300 bg-white px-6 py-4 text-lg font-medium text-gray-900 transition-all hover:border-gray-400 hover:shadow-md disabled:opacity-60 disabled:cursor-wait"
      >
        {isRedirecting ? (
          <CircularProgress size={22} sx={{ color: "#d62976" }} />
        ) : (
          <span
            className="w-7 h-7 rounded-lg flex items-center justify-center text-white"
            style={{ background: INSTAGRAM_GRADIENT }}
          >
            <FaInstagram size={18} />
          </span>
        )}
        {isRedirecting ? "Открываем Instagram…" : "Продолжить с Instagram"}
      </button>

      <div className="flex items-center justify-center gap-2 mt-4 text-xs text-gray-400">
        <FaLock size={11} />
        <span>Вход проходит на сайте Instagram — мы не видим ваш пароль</span>
      </div>
    </InstagramConnectLayout>
  );
};

export default InstagramConnectPage;
